import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, House, Package, Plus, Star, Trash2, X } from 'lucide-react';
import { useWarehouseStore } from '../../store/warehouseStore';
import { useProjectStore } from '../../store/projectStore';

interface Props {
  onClose: () => void;
}

// Smooth collapsible section using the CSS grid-rows trick (handles unknown heights)
const Collapsible: React.FC<{ open: boolean; children: React.ReactNode }> = ({ open, children }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateRows: open ? '1fr' : '0fr',
      transition: 'grid-template-rows 220ms cubic-bezier(0.4,0,0.2,1)',
    }}
  >
    <div style={{ overflow: 'hidden' }}>{children}</div>
  </div>
);

const PreferencesDrawer: React.FC<Props> = ({ onClose }) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';

  const {
    warehouses,
    defaultWarehouseId,
    storageAreasByWarehouse,
    defaultStorageAreaIdByWarehouse,
    addWarehouse,
    removeWarehouse,
    renameWarehouse,
    reorderWarehouses,
    setDefaultWarehouse,
    addStorageArea,
    removeStorageArea,
    renameStorageArea,
    reorderStorageAreas,
    setDefaultStorageArea,
  } = useWarehouseStore();

  // Entrance / exit animation
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // Next frame → trigger slide-in
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 260); // wait for slide-out to finish
  };

  // Which warehouses have their storage-area list expanded
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(warehouses.map(w => [w.id, true]))
  );
  const [addingAreaFor, setAddingAreaFor] = useState<string | null>(null);
  const [newAreaName, setNewAreaName] = useState('');
  const [addingWarehouse, setAddingWarehouse] = useState(false);
  const [newWarehouseName, setNewWarehouseName] = useState('');
  const [confirmDeleteWarehouse, setConfirmDeleteWarehouse] = useState<string | null>(null);
  const [confirmDeleteArea, setConfirmDeleteArea] = useState<{ warehouseId: string; areaId: string } | null>(null);

  // Drag-to-reorder state for warehouses
  const [draggingWarehouseIndex, setDraggingWarehouseIndex] = useState<number | null>(null);
  const [dragOverWarehouseIndex, setDragOverWarehouseIndex] = useState<number | null>(null);

  // Drag-to-reorder state for storage areas
  const [draggingArea, setDraggingArea] = useState<{ warehouseId: string; index: number } | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Inline rename state
  const [editingWarehouseId, setEditingWarehouseId] = useState<string | null>(null);
  const [editingWarehouseName, setEditingWarehouseName] = useState('');
  const [editingArea, setEditingArea] = useState<{ warehouseId: string; areaId: string } | null>(null);
  const [editingAreaName, setEditingAreaName] = useState('');

  const newWarehouseRef = useRef<HTMLInputElement>(null);
  const newAreaRef = useRef<HTMLInputElement>(null);
  const editWarehouseRef = useRef<HTMLInputElement>(null);
  const editAreaRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (addingWarehouse) newWarehouseRef.current?.focus(); }, [addingWarehouse]);
  useEffect(() => { if (addingAreaFor) newAreaRef.current?.focus(); }, [addingAreaFor]);
  useEffect(() => { if (editingWarehouseId) editWarehouseRef.current?.select(); }, [editingWarehouseId]);
  useEffect(() => { if (editingArea) editAreaRef.current?.select(); }, [editingArea]);

  const commitWarehouseRename = () => {
    if (editingWarehouseId) renameWarehouse(editingWarehouseId, editingWarehouseName);
    setEditingWarehouseId(null);
  };

  const commitAreaRename = () => {
    if (editingArea) renameStorageArea(editingArea.warehouseId, editingArea.areaId, editingAreaName);
    setEditingArea(null);
  };

  const toggleExpand = (id: string) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const submitWarehouse = () => {
    const name = newWarehouseName.trim();
    if (name) addWarehouse(name);
    setNewWarehouseName('');
    setAddingWarehouse(false);
  };

  const submitArea = (warehouseId: string) => {
    const name = newAreaName.trim();
    if (name) addStorageArea(warehouseId, name);
    setNewAreaName('');
    setAddingAreaFor(null);
  };

  // Theme tokens
  const bg      = isDark ? '#1c2029' : '#ffffff';
  const surface = isDark ? '#20242b' : '#f9fafb';
  const border  = isDark ? '#343a43' : '#e5e7eb';
  const text    = isDark ? '#f3f4f6' : '#111827';
  const muted   = isDark ? '#9ca3af' : '#6b7280';
  const inputBg = isDark ? '#171a1f' : '#f3f4f6';
  const hoverBg = isDark ? '#2b3140' : '#f3f4f6';

  const TRANSITION = 'transform 260ms cubic-bezier(0.4,0,0.2,1)';

  return (
    <>
      {/* Backdrop — fades in/out */}
      <div
        className="fixed inset-0 z-40"
        style={{
          backgroundColor: 'rgba(0,0,0,0.35)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 260ms ease',
        }}
        onMouseDown={handleClose}
      />

      {/* Drawer panel — slides in from right */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 flex flex-col w-96 shadow-2xl"
        style={{
          backgroundColor: bg,
          borderLeft: `1px solid ${border}`,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: TRANSITION,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${border}` }}
        >
          <span className="text-sm font-semibold" style={{ color: text }}>Edit Warehouses</span>
          <button
            onClick={handleClose}
            className="p-1 rounded transition-opacity hover:opacity-70"
            style={{ color: muted }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">

          <p className="text-[11px] uppercase tracking-widest font-semibold mb-3" style={{ color: muted }}>
            Warehouses
          </p>

          {warehouses.length === 0 && (
            <p className="text-xs py-6 text-center" style={{ color: muted }}>
              No warehouses yet. Add one below.
            </p>
          )}

          {warehouses.map((warehouse, warehouseIndex) => {
            const isDefault       = defaultWarehouseId === warehouse.id;
            const areas           = storageAreasByWarehouse[warehouse.id] ?? [];
            const isExpanded      = expanded[warehouse.id] ?? true;
            const isConfirmDelete = confirmDeleteWarehouse === warehouse.id;
            const isDraggingThisWh  = draggingWarehouseIndex === warehouseIndex;
            const isDragOverWh      = dragOverWarehouseIndex === warehouseIndex;

            return (
              <div
                key={warehouse.id}
                draggable
                onDragStart={() => setDraggingWarehouseIndex(warehouseIndex)}
                onDragEnd={() => { setDraggingWarehouseIndex(null); setDragOverWarehouseIndex(null); }}
                onDragOver={e => { e.preventDefault(); setDragOverWarehouseIndex(warehouseIndex); }}
                onDrop={() => {
                  if (draggingWarehouseIndex !== null && draggingWarehouseIndex !== warehouseIndex) {
                    reorderWarehouses(draggingWarehouseIndex, warehouseIndex);
                  }
                  setDraggingWarehouseIndex(null);
                  setDragOverWarehouseIndex(null);
                }}
                className="rounded-lg overflow-hidden transition-colors"
                style={{
                  border: `1px solid ${isDragOverWh ? (isDark ? '#4a90d9' : '#3b82f6') : border}`,
                  backgroundColor: surface,
                  opacity: isDraggingThisWh ? 0.4 : 1,
                }}
              >
                {/* Warehouse header row */}
                <div
                  className="flex items-center gap-1 px-3 py-2.5 group/wh"
                  style={{ backgroundColor: isDark ? '#2b3140' : '#f0f1f3', cursor: 'grab' }}
                >
                  <GripVertical size={13} className="flex-shrink-0 opacity-0 group-hover/wh:opacity-40 transition-opacity flex-shrink-0" style={{ color: muted }} />
                  <button
                    onClick={() => toggleExpand(warehouse.id)}
                    className="p-0.5 rounded flex-shrink-0 transition-opacity hover:opacity-60"
                    style={{ color: muted }}
                  >
                    <ChevronDown
                      size={13}
                      style={{
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
                      }}
                    />
                  </button>

                  <House size={13} className="flex-shrink-0" style={{ color: muted }} />
                  {editingWarehouseId === warehouse.id ? (
                    <input
                      ref={editWarehouseRef}
                      type="text"
                      value={editingWarehouseName}
                      onChange={e => setEditingWarehouseName(e.target.value)}
                      onBlur={commitWarehouseRename}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitWarehouseRename();
                        if (e.key === 'Escape') setEditingWarehouseId(null);
                      }}
                      className="flex-1 text-sm font-medium px-1 py-0 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
                      style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                    />
                  ) : (
                    <span
                      className="flex-1 text-sm font-medium truncate cursor-text"
                      style={{ color: text }}
                      onDoubleClick={() => {
                        setEditingWarehouseId(warehouse.id);
                        setEditingWarehouseName(warehouse.name);
                      }}
                      title="Double-click to rename"
                    >
                      {warehouse.name}
                    </span>
                  )}

                  <button
                    onClick={() => setDefaultWarehouse(warehouse.id)}
                    title={isDefault ? 'Remove as current warehouse' : 'Set as current warehouse'}
                    className={`p-1 rounded flex-shrink-0 transition-all hover:opacity-80 ${
                      isDefault ? 'opacity-100' : 'opacity-0 group-hover/wh:opacity-100'
                    }`}
                    style={{ color: isDefault ? '#f59e0b' : muted }}
                  >
                    <Star size={13} fill={isDefault ? '#f59e0b' : 'none'} strokeWidth={isDefault ? 0 : 2} />
                  </button>

                  {isConfirmDelete ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => { removeWarehouse(warehouse.id); setConfirmDeleteWarehouse(null); }}
                        className="text-[10px] px-2 py-0.5 rounded bg-red-500/80 hover:bg-red-600 text-white transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmDeleteWarehouse(null)}
                        className="text-[10px] px-2 py-0.5 rounded transition-colors"
                        style={{ color: muted, backgroundColor: hoverBg }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteWarehouse(warehouse.id)}
                      className="p-1 rounded flex-shrink-0 transition-all hover:text-red-400 opacity-0 group-hover/wh:opacity-100"
                      style={{ color: muted }}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>

                {/* Storage areas — animated collapse */}
                <Collapsible open={isExpanded}>
                  <div style={{ borderTop: `1px solid ${border}` }}>
                    {areas.length === 0 && addingAreaFor !== warehouse.id && (
                      <p className="text-[11px] px-10 py-2" style={{ color: muted }}>
                        No storage areas
                      </p>
                    )}

                    {areas.map((area, areaIndex) => {
                      const isAreaDefault    = defaultStorageAreaIdByWarehouse[warehouse.id] === area.id;
                      const isConfirmAreaDel =
                        confirmDeleteArea?.warehouseId === warehouse.id &&
                        confirmDeleteArea?.areaId === area.id;
                      const isDraggingThis =
                        draggingArea?.warehouseId === warehouse.id && draggingArea?.index === areaIndex;
                      const isDragOver =
                        draggingArea?.warehouseId === warehouse.id && dragOverIndex === areaIndex;

                      return (
                        <div
                          key={area.id}
                          draggable
                          onDragStart={() => setDraggingArea({ warehouseId: warehouse.id, index: areaIndex })}
                          onDragEnd={() => { setDraggingArea(null); setDragOverIndex(null); }}
                          onDragOver={e => { e.preventDefault(); setDragOverIndex(areaIndex); }}
                          onDrop={() => {
                            if (
                              draggingArea &&
                              draggingArea.warehouseId === warehouse.id &&
                              draggingArea.index !== areaIndex
                            ) {
                              reorderStorageAreas(warehouse.id, draggingArea.index, areaIndex);
                            }
                            setDraggingArea(null);
                            setDragOverIndex(null);
                          }}
                          className="flex items-center gap-1 pl-7 pr-3 py-2 group transition-colors"
                          style={{
                            borderTop: `1px solid ${border}`,
                            opacity: isDraggingThis ? 0.4 : 1,
                            backgroundColor: isDragOver ? (isDark ? '#2b3140' : '#e9eef6') : 'transparent',
                            cursor: 'grab',
                          }}
                        >
                          <GripVertical size={12} className="flex-shrink-0 opacity-0 group-hover:opacity-40 transition-opacity cursor-grab" style={{ color: muted }} />
                          <Package size={11} className="flex-shrink-0" style={{ color: muted }} />
                          {editingArea?.areaId === area.id && editingArea?.warehouseId === warehouse.id ? (
                            <input
                              ref={editAreaRef}
                              type="text"
                              value={editingAreaName}
                              onChange={e => setEditingAreaName(e.target.value)}
                              onBlur={commitAreaRename}
                              onKeyDown={e => {
                                if (e.key === 'Enter') commitAreaRename();
                                if (e.key === 'Escape') setEditingArea(null);
                              }}
                              className="flex-1 text-xs px-1 py-0 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
                              style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                            />
                          ) : (
                            <span
                              className="flex-1 text-xs truncate cursor-text"
                              style={{ color: muted }}
                              onDoubleClick={() => {
                                setEditingArea({ warehouseId: warehouse.id, areaId: area.id });
                                setEditingAreaName(area.name);
                              }}
                              title="Double-click to rename"
                            >
                              {area.name}
                            </span>
                          )}

                          {areas.length > 1 && (
                            <button
                              onClick={() => setDefaultStorageArea(warehouse.id, area.id)}
                              title={isAreaDefault ? 'Remove default area' : 'Set as default area'}
                              className="p-0.5 rounded opacity-0 group-hover:opacity-100 flex-shrink-0 transition-all"
                              style={{ color: isAreaDefault ? '#f59e0b' : muted }}
                            >
                              <Star size={11} fill={isAreaDefault ? '#f59e0b' : 'none'} />
                            </button>
                          )}

                          {isConfirmAreaDel ? (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => { removeStorageArea(warehouse.id, area.id); setConfirmDeleteArea(null); }}
                                className="text-[10px] px-2 py-0.5 rounded bg-red-500/80 hover:bg-red-600 text-white transition-colors"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmDeleteArea(null)}
                                className="text-[10px] px-2 py-0.5 rounded transition-colors"
                                style={{ color: muted, backgroundColor: hoverBg }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteArea({ warehouseId: warehouse.id, areaId: area.id })}
                              className="p-0.5 rounded opacity-0 group-hover:opacity-100 flex-shrink-0 transition-all hover:text-red-400"
                              style={{ color: muted }}
                            >
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {addingAreaFor === warehouse.id ? (
                      <div className="flex items-center gap-1.5 px-3 py-2" style={{ borderTop: `1px solid ${border}` }}>
                        <input
                          ref={newAreaRef}
                          type="text"
                          value={newAreaName}
                          onChange={e => setNewAreaName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter')  submitArea(warehouse.id);
                            if (e.key === 'Escape') { setAddingAreaFor(null); setNewAreaName(''); }
                          }}
                          placeholder="Storage area name…"
                          className="flex-1 text-xs px-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                          style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                        />
                        <button
                          onClick={() => submitArea(warehouse.id)}
                          className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors flex-shrink-0"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => { setAddingAreaFor(null); setNewAreaName(''); }}
                          className="p-1 rounded hover:opacity-70 flex-shrink-0"
                          style={{ color: muted }}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setAddingAreaFor(warehouse.id); setNewAreaName(''); }}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-1 text-[11px] uppercase tracking-wide transition-opacity hover:opacity-80"
                        style={{ color: muted, borderTop: `1px solid ${border}` }}
                      >
                        <Plus size={11} />
                        Add storage area
                      </button>
                    )}
                  </div>
                </Collapsible>
              </div>
            );
          })}

          {/* Add warehouse */}
          {addingWarehouse ? (
            <div
              className="flex items-center gap-1.5 rounded-lg px-3 py-2.5"
              style={{ border: `1px dashed ${border}`, backgroundColor: surface }}
            >
              <input
                ref={newWarehouseRef}
                type="text"
                value={newWarehouseName}
                onChange={e => setNewWarehouseName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter')  submitWarehouse();
                  if (e.key === 'Escape') { setAddingWarehouse(false); setNewWarehouseName(''); }
                }}
                placeholder="Warehouse name…"
                className="flex-1 text-sm px-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
              />
              <button
                onClick={submitWarehouse}
                className="text-xs px-2.5 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors flex-shrink-0"
              >
                Add
              </button>
              <button
                onClick={() => { setAddingWarehouse(false); setNewWarehouseName(''); }}
                className="p-1 rounded hover:opacity-70 flex-shrink-0"
                style={{ color: muted }}
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setAddingWarehouse(true)}
              className="w-full flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm transition-opacity hover:opacity-80"
              style={{ border: `1px dashed ${border}`, color: muted, backgroundColor: 'transparent' }}
            >
              <Plus size={14} />
              Add Warehouse
            </button>
          )}
        </div>

      </div>
    </>
  );
};

export default PreferencesDrawer;
